import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { useWorkspaceId } from '@/contexts/WorkspaceContext';
import { isLaunched } from '@/lib/campaignStatus';

// Keeps the old builder URLs working.
//
// `/create` and `/create?id=<campaignId>` were the only way into the campaign builder for the whole
// life of the product, so those links are in people's history, their bookmarks, and any email that
// ever pointed at a campaign. Splitting one entity into two moved the destination, and the mapping
// is not a rewrite rule: a draft's creative now lives at /campaigns/media/:mediaId while a
// launched campaign belongs at /campaigns/:campaignId, and only the server knows which a given
// id is.
//
// This resolves that in one request. It works at all because the backfill guarantees every campaign
// has a media item - there is no id that lands here and cannot be sent somewhere sensible.
export default function CreateRedirect() {
  const navigate = useNavigate();
  const workspaceId = useWorkspaceId();
  const [searchParams] = useSearchParams();
  const campaignId = searchParams.get('id');

  useEffect(() => {
    if (!workspaceId) return;

    // No id: this was "start something new", which now begins with the creative.
    if (!campaignId) {
      navigate(`/w/${workspaceId}/campaigns/media`, { replace: true });
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const { campaign } = await apiFetch<{
          campaign: { id: string; status: string; campaignMediaId: string | null };
        }>(`/workspaces/${workspaceId}/campaigns/${campaignId}`);
        if (cancelled) return;

        // A launched campaign's page shows what it sent and how it did. A draft's creative is what
        // the old URL was actually for, so that goes to the builder.
        if (isLaunched(campaign.status) || !campaign.campaignMediaId) {
          navigate(`/w/${workspaceId}/campaigns/${campaign.id}`, { replace: true });
        } else {
          navigate(`/w/${workspaceId}/campaigns/media/${campaign.campaignMediaId}`, { replace: true });
        }
      } catch {
        // The id may name something deleted, or belong to another workspace. The library is a
        // better landing place than an error for a link someone followed from their history.
        if (!cancelled) navigate(`/w/${workspaceId}/campaigns/media`, { replace: true });
      }
    })();

    return () => { cancelled = true; };
  }, [workspaceId, campaignId, navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
    </div>
  );
}
